import { lazy, Suspense, useEffect, useState } from "react";
import { Link, RouteObject, useRoutes } from "react-router-dom";
import { useLazyQuery } from "@apollo/client/react";
import { gql } from "@apollo/client";

import { useAuth, isAuthorizedForResource } from "../../auth";

import PageLayout from "../../../layouts/PageLayout";
import {
  LayoutSplashScreen,
  NotFound,
  NotAuthorized,
  Head,
} from "../../../components/core";

import { DockListResource } from "./docks/List";
import { DeliveryRunListResource } from "./delivery-runs/List";
import { classNames } from "../../../utils";

const DockList = lazy(() => import("./docks/List"));
const DeliveryRunList = lazy(() => import("./delivery-runs/List"));

const FETCH_DOCKS = gql`
  query FetchDocks {
    fetchDocks {
      id
      name
      createdAt
      status
    }
  }
`;

const FETCH_DELIVERYRUNS = gql`
  query FetchDeliveryRuns {
    fetchDeliveryRuns {
      id
      name
      dock {
        id
        name
      }
      createdAt
      status
    }
  }
`;

export default function Settings({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) {
  const { currentRole } = useAuth();

  let routes: RouteObject[] = [
    {
      element: <PageLayout />,
      children: [
        {
          index: true,
          element: (
            <>
              <Head
                title="Fleets"
                heading="Fleet Settings"
                breadcrumbs={[
                  ...breadcrumbs,
                  {
                    name: "Fleets",
                    href: "/fleets",
                  },
                  {
                    name: "Fleet Settings",
                    href: null,
                  },
                ]}
              />
              <Layout />
            </>
          ),
        },
        {
          path: DockListResource.path,
          element: isAuthorizedForResource(
            currentRole,
            DockListResource.access
          ) ? (
            <Suspense fallback={<LayoutSplashScreen />}>
              <DockList breadcrumbs={breadcrumbs} />
            </Suspense>
          ) : (
            <NotAuthorized
              error={new Error(DockListResource.access.join(", "))}
            />
          ),
        },
        {
          path: DeliveryRunListResource.path,
          element: isAuthorizedForResource(
            currentRole,
            DeliveryRunListResource.access
          ) ? (
            <Suspense fallback={<LayoutSplashScreen />}>
              <DeliveryRunList breadcrumbs={breadcrumbs} />
            </Suspense>
          ) : (
            <NotAuthorized
              error={new Error(DeliveryRunListResource.access.join(", "))}
            />
          ),
        },
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];

  return useRoutes(routes);
}

function Layout() {
  const [navigation, setNavigation] = useState([
    {
      id: "docks",
      name: "Docks",
      path: "/fleets/settings/docks",
      icon: "bi bi-hdd-stack",
      count: 0,
    },
    {
      id: "delivery-runs",
      name: "Delivery Runs",
      path: "/fleets/settings/delivery-runs",
      icon: "bi bi-truck-front",
      count: 0,
    },
  ]);

  const [fetchDocks] = useLazyQuery(FETCH_DOCKS);
  const [fetchDeliveryRuns] = useLazyQuery(FETCH_DELIVERYRUNS);

  useEffect(() => {
    fetchDocks({
      onCompleted: (data) => {
        setNavigation((navigation) =>
          navigation.map((item) => {
            if (item.id === "docks") {
              return {
                ...item,
                count: data.fetchDocks.length,
              };
            }

            return item;
          })
        );
      },
    });

    fetchDeliveryRuns({
      onCompleted: (data) => {
        setNavigation((navigation) =>
          navigation.map((item) => {
            if (item.id === "delivery-runs") {
              return {
                ...item,
                count: data.fetchDeliveryRuns.length,
              };
            }

            return item;
          })
        );
      },
    });
  }, [fetchDeliveryRuns, fetchDocks]);

  return (
    <>
      <div>
        <h2 className="text-sm font-medium text-gray-500">Settings</h2>
        <ul className="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          {navigation.map((nav) => (
            <Link
              key={nav.name}
              to={nav.path}
              className="overflow-hidden rounded-lg border border-gray-300 bg-white hover:border-primary-600"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-300">
                      <span
                        className={classNames(
                          "h-6 w-6 flex-shrink-0 text-center text-xl leading-6 text-current",
                          nav.icon
                        )}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="ml-4 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500">
                        {nav.name}
                      </dt>
                      <dd>
                        <div className="text-lg font-medium text-gray-900">
                          {nav.count}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-5 py-3">
                <div className="text-sm">
                  <span className="font-medium text-primary-700 hover:text-primary-900">
                    View all
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </ul>
      </div>
    </>
  );
}

export const SettingsResource: ResourceProps = {
  name: "Fleet Settings",
  description: "Fleet Settings",
  access: ["read-fleets"],
  path: "settings/*",
};
